// Report 54B (D2): corrections to a paid payslip, in the words each reader uses. Every state and amount is the
// server's; this file only names them. Three states are kept apart on purpose: where the case is in its workflow,
// what is decided about tax and contributions, and where the money is. "Approved" is never "paid".

import { request } from "../../../../services/api";
import { createClientRequestId } from "../../../../utils/clientRequestId";
import { formatMoney } from "../../../../components/ui/Money";

const BASE = "/api/business/hr/payroll/adjustments";
const post = (url, data, key) => request({ url, method: "POST", data, headers: key ? { "Idempotency-Key": key } : undefined });
const unwrap = (res) => res?.data ?? res;

/** One key per attempt at a money or create command, so a double press or a retry does nothing twice. */
export const newRequestKey = () => createClientRequestId("adj");

export const adjustmentsApi = {
  list: async (params) => unwrap(await request({ url: BASE, method: "GET", params })) || [],
  get: async (id) => unwrap(await request({ url: `${BASE}/${id}`, method: "GET" })),
  mine: async (params) => unwrap(await request({ url: `${BASE}/mine`, method: "GET", params })) || [],
  preview: async (data) => unwrap(await post(`${BASE}/preview`, data)),
  create: async (data, key) => unwrap(await post(BASE, data, key)),
  submit: (id) => post(`${BASE}/${id}/submit`),
  cancel: (id, reason) => post(`${BASE}/${id}/cancel`, { reason }),
  reverse: async (id, data, key) => unwrap(await post(`${BASE}/${id}/reverse`, data, key)),
  reviewStatutory: (id, data) => post(`${BASE}/${id}/statutory-review`, data),
  approve: (id, remarks) => post(`${BASE}/${id}/approve`, { remarks }),
  returnToHr: (id, remarks) => post(`${BASE}/${id}/return`, { remarks }),
  reject: (id, remarks) => post(`${BASE}/${id}/reject`, { remarks }),
  cashHandover: (id, data, key) => post(`${BASE}/${id}/cash-handover`, data, key),
  confirmOnBehalf: (id, reason) => post(`${BASE}/${id}/confirm-on-behalf`, { reason }),
  cashReturn: (id, data, key) => post(`${BASE}/${id}/cash-return`, data, key),
  resolveDispute: (id, data) => post(`${BASE}/${id}/resolve-dispute`, data),
  confirmReceipt: (id) => post(`${BASE}/mine/${id}/confirm-receipt`),
  acknowledge: (id) => post(`${BASE}/mine/${id}/acknowledge`),
  dispute: (id, reason) => post(`${BASE}/mine/${id}/dispute`, { reason }),
};

export const HR_CORRECTIONS_PATH = "/business/hr/payroll/corrections";
export const FINANCE_CORRECTIONS_PATH = "/business/finance/payroll/corrections";

export const CLASSIFICATION = {
  WAGE_UNDERPAYMENT: { label: "Underpaid wages", hint: "Pay that was earned but left out: an absence counted in error, missing approved overtime, leave treated as unpaid." },
  WAGE_OVERPAYMENT: { label: "Overpaid wages", hint: "Pay that was not earned: an extra paid day, overtime that was not worked." },
  CASH_HANDOVER_DISCREPANCY: { label: "Wrong cash handed over", hint: "The payslip is right, but the cash in hand was more or less than its net pay." },
  DEDUCTION_ERROR: { label: "Deduction taken in error", hint: "A loan installment or another deduction that should not have been taken on this payslip." },
  STATUTORY_OR_WITHHOLDING_ERROR: { label: "Contribution or tax amount", hint: "An SSS, PhilHealth, Pag-IBIG or withholding tax amount believed to be wrong." },
};
export const classificationLabel = (value) => CLASSIFICATION[value]?.label || "Correction";

const TAX = {
  NOT_AFFECTED: "Tax and contributions not affected",
  REVIEW_REQUIRED: "Tax and contributions not decided yet",
  REVIEWED: "Tax and contributions reviewed",
};
export const taxLabel = (item) => TAX[item?.statutoryState] || "";

/** The one state a list row shows, and what happens next, for staff. `tone` follows DESIGN.md (words first). */
export function stateOf(item) {
  const pay = item.direction === "PAY_EMPLOYEE";
  switch (item.status) {
    case "DRAFT": return { label: "Draft", tone: "neutral", next: "HR sends it to Finance." };
    case "RETURNED": return { label: "Returned to HR", tone: "warning", next: "HR answers Finance and sends it again, or cancels it." };
    case "REJECTED": return { label: "Rejected by Finance", tone: "neutral", next: "Nothing is paid or recovered." };
    case "CANCELLED": return { label: "Cancelled", tone: "neutral", next: "Nothing is paid or recovered." };
    case "SUBMITTED":
      return item.statutoryState === "REVIEW_REQUIRED"
        ? { label: "Awaiting tax review", tone: "warning", next: "Finance records how tax and contributions are treated. Until then it cannot be approved, paid or recovered." }
        : { label: "Awaiting Finance approval", tone: "warning", next: "Someone in Finance other than the person who raised it approves, returns or rejects it." };
    default: break;
  }
  // APPROVED: the money state speaks.
  if (item.employeeResponse === "DISPUTED") return { label: "Disputed by the employee", tone: "danger", next: "Finance answers the dispute. No money is recorded against the claim until then." };
  switch (item.settlementStatus) {
    case "OPEN": return pay
      ? { label: "Approved — awaiting payment", tone: "warning", next: "Finance hands over the cash and records it." }
      : { label: "Approved — recovery outstanding", tone: "warning", next: item.recoveryMethod === "FUTURE_PAY" ? "An installment is taken from each coming salary; the balance falls when that salary is paid." : "Finance records the cash when the employee returns it." };
    case "HANDED_OVER": return { label: "Cash handed over — awaiting the employee's confirmation", tone: "warning", next: "The employee confirms receiving it. Only then is it paid." };
    case "PARTIAL": return { label: "Partly recovered", tone: "warning", next: item.recoveryMethod === "FUTURE_PAY" ? "The next installment is taken from the next salary." : "Finance records the rest when the employee returns it." };
    case "SETTLED": return pay ? { label: "Paid — confirmed by the employee", tone: "success", next: "Nothing more to do." } : { label: "Recovered in full", tone: "success", next: "Nothing more to do." };
    case "CANCELLED": return { label: "Cancelled", tone: "neutral", next: "Nothing is paid or recovered." };
    default: return { label: "Approved", tone: "neutral", next: "" };
  }
}

/** The same case in the employee's words. */
export function myStateOf(item) {
  const amount = formatMoney(item.outstandingCents || item.amountCents);
  if (item.direction === "PAY_EMPLOYEE") {
    if (item.settlementStatus === "SETTLED") return { label: "Paid", tone: "success", detail: `You confirmed receiving ${formatMoney(item.settledCents)}.` };
    if (item.settlementStatus === "HANDED_OVER") return { label: "Confirm receipt", tone: "warning", detail: `Finance recorded handing you ${formatMoney(item.amountCents)} in cash. Confirm once you have received it.` };
    return { label: "Payment pending", tone: "neutral", detail: `${formatMoney(item.amountCents)} is owed to you. Finance has not recorded handing it over yet.` };
  }
  if (item.settlementStatus === "SETTLED") return { label: "Settled", tone: "success", detail: `${formatMoney(item.settledCents)} was recovered. Nothing is outstanding.` };
  if (item.settlementStatus === "CANCELLED") return { label: "Withdrawn", tone: "neutral", detail: "This claim was withdrawn. Nothing is owed." };
  if (item.employeeResponse === "DISPUTED") return { label: "Disputed by you", tone: "warning", detail: "Finance will answer your dispute. Nothing more is taken or recorded until then." };
  return {
    label: item.settlementStatus === "PARTIAL" ? "Partly returned" : "Outstanding",
    tone: "warning",
    detail: item.recoveryMethod === "FUTURE_PAY"
      ? `${amount} is still to be recovered, ${formatMoney(item.installmentCents)} from each coming salary. It goes down only when that salary is paid.`
      : `${amount} is still to be returned in cash. It goes down only when Finance records receiving it.`,
  };
}

const EVENT = {
  RAISED: "Raised", REVERSAL_RAISED: "Reversal raised", SUBMITTED: "Sent to Finance", STATUTORY_REVIEWED: "Tax treatment recorded",
  APPROVED: "Approved", RETURNED: "Returned to HR", REJECTED: "Rejected", CANCELLED: "Cancelled", CASH_HANDED_OVER: "Cash handed over",
  RECEIPT_CONFIRMED: "Receipt confirmed by the employee", RECEIPT_CONFIRMED_ON_BEHALF: "Receipt confirmed by the owner for the employee",
  CASH_RETURNED: "Cash received from the employee", INSTALLMENT_RECOVERED: "Installment recovered with a paid salary",
  ACKNOWLEDGED: "Acknowledged by the employee", DISPUTED: "Disputed by the employee", DISPUTE_UPHELD_CLAIM: "Dispute answered: the claim stands",
  DISPUTE_CLAIM_WITHDRAWN: "Dispute answered: the rest of the claim was withdrawn",
};
export const eventLabel = (action) => EVENT[action] || String(action || "").toLowerCase().replace(/_/g, " ");

export function when(value) {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString("en-PH", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

/** Pesos typed by a person -> whole centavos, or null when it is not an amount. */
export function toCents(value) {
  const clean = String(value ?? "").replace(/[,₱\s]/g, "");
  if (!/^\d+(\.\d{1,2})?$/.test(clean)) return null;
  return Math.round(Number(clean) * 100);
}
